import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { useStreamingText } from '../hooks/useStreamingText'
import { onToolActivity } from '../lib/toolRunner'
import { LiveToolCard } from './LiveToolCard'
import { MessageBody } from './MessageBody'

/**
 * Akan asistan mesaji - store'a abone olan TEK bilesen.
 *
 * Store'a `useSyncExternalStore` ile yalnizca burasi baglaniyor. Sonuc: 3000
 * token'lik bir yanit saniyede ~75 store yazmasi uretse bile re-render eden
 * agac bu alt agac; sidebar, composer ve mesaj listesinin geri kalani hic
 * dokunulmuyor.
 */
export function StreamingMessage({
  runId,
  onGrow,
}: {
  runId: string
  /**
   * Metin her buyudugunde (token basina) cagriliyor. Bu bilesen store'a
   * DOGRUDAN abone (`useSyncExternalStore`) - ust bilesen (`ConversationView`)
   * token basina re-render OLMUYOR, yani "kullanici dipteyse otomatik
   * takip et" kararini burada, her buyumede kendimiz tetiklemek zorundayiz.
   */
  onGrow?: () => void
}) {
  const { t } = useTranslation()
  const view = useStreamingText(runId)
  const toolBusy = useToolActivity()
  const queued = view.queuePosition !== null && view.queuePosition > 1

  // `view.text` degistikce (token basina) ust bilesene "buyudum, dipteysen
  // takip et" haberi veriliyor. `useLayoutEffect` DEGIL `useEffect`: kaydirma
  // burada kritik yol degil, bir frame gecikme fark edilmiyor ama layout
  // efekti token basina zorlamak gereksiz maliyet olurdu.
  useEffect(() => {
    onGrow?.()
  }, [view.text, onGrow])

  return (
    <div className="msg-row">
      <span className="msg-avatar" aria-hidden="true">
        {t('chat.avatar.assistant')}
      </span>
      <article className="msg assistant">
        {queued && <QueueBanner position={view.queuePosition!} />}
        {!queued && !view.text && !view.error && <Waiting />}
        {toolBusy && !view.done && !view.live && <ToolBanner />}

        <MessageBody text={view.text} streaming={!view.done} />
        {view.live && !view.done && <LiveToolCard live={view.live} />}

        {view.error && (
          <p className="msg-error" role="alert">
            {view.error}
          </p>
        )}
        {view.model && view.done && !view.error && (
          <footer className="msg-meta">{label(view.model, t)}</footer>
        )}
      </article>
    </div>
  )
}

/**
 * Kuyruk bilgisi.
 *
 * Bekleme bu uygulamada NORMAL durum: tek GPU, olculmus 8.1 tok/s. Okunabilir
 * bir bekleme, bozuk bir uygulamadan ayirt edilebilir olmali - bannerin varlik
 * sebebi bu ve plandaki en yuksek algilanan-kalite kazanci.
 */
function QueueBanner({ position }: { position: number }) {
  const { t } = useTranslation()
  return (
    <p className="queue" role="status">
      {t('chat.queue.position', { position })}
    </p>
  )
}

/**
 * Kod calistirma etkinligi.
 *
 * Kod TARAYICIDA kosuyor (sunucuda degil - gerekce `lib/pyodide.ts`de) ve ilk
 * kullanimda Python ortami ~13.5 MB indirip aciliyor: olculen ~2-5 saniye.
 * O sure boyunca hicbir token gelmiyor, yani kullanicinin gordugu sey donmus
 * bir arayuz. Bu satir olmadan "takildi mi" ile "calisiyor" ayirt edilemiyor -
 * kuyruk bannerinin varlik sebebiyle birebir ayni gerekce.
 */
function useToolActivity(): boolean {
  const [busy, setBusy] = useState(false)
  // Store'a degil bir olay kaynagina abone oluyoruz; `useSyncExternalStore`
  // yerine `useState` yeterli cunku bu deger saniyede bir kez degil, cagri
  // basina iki kez degisiyor.
  useEffect(() => onToolActivity(setBusy), [])
  return busy
}

function ToolBanner() {
  const { t } = useTranslation()
  return (
    <p className="queue" role="status">
      {t('chat.tool.running')}
    </p>
  )
}

function Waiting() {
  // Sifir byte'lik bir bekleme (model yuklemesi 20 sn surebiliyor) olu bir
  // baglanti gibi gorunuyor; bu satir onu ayirt ediyor. Noktalar SADECE
  // gorsel - ekran okuyucu `queue` metnini okuyor, `aria-hidden` bu yuzden.
  const { t } = useTranslation()
  return (
    <p className="queue" role="status">
      <span className="typing-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>{' '}
      {t('chat.waiting')}
    </p>
  )
}

function label(model: string, t: TFunction): string {
  if (model === 'reason') return t('chat.model.reason')
  if (model === 'fast') return t('chat.model.fast')
  // Bilinmeyen bir model kimligi OLDUGU GIBI gosteriliyor: bir gun ucuncu
  // bir model eklenirse etiketi eksik olur ama gorunur - sessizce bos
  // birakmaktan iyi.
  return model
}
